import React, { useEffect, useContext, useState, useCallback } from 'react';
import theme from 'theme';
import i18n from 'i18n-js';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import { Table } from 'components/UI';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import { SearchField } from 'components/FormFields';
import { truncateKeywordSpaces } from 'utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardActions } from 'state/modules/dashboard';
import { Grid, Box, Paper, Typography, Tooltip } from '@mui/material';

const TableLabel = ({ title, subtitle, description, total, align = 'center' }) => (
  <Box display="flex" flexDirection="column" alignSelf="flex-start">
    <Box
      height={30}
      display="flex"
      alignItems="center"
      justifyContent={align}
      sx={{ color: 'common.black', fontSize: 12, fontWeight: 'bold' }}
    >
      {title}
    </Box>
    <Box
      alignItems="start"
      sx={{ display: 'flex', height: 15, justifyContent: 'center', alignItems: align }}
    >
      <Typography
        fontSize={12}
        fontWeight="bold"
        color="general.grey70"
        sx={{ alignItems: 'center', display: 'flex', lineHeight: 1, textAlign: 'center' }}
      >
        {subtitle}
      </Typography>
      <Tooltip title={description} placement="right">
        <InfoIcon sx={{ marginLeft: '2px', color: 'general.grey70', fontSize: '0.875rem' }} />
      </Tooltip>
    </Box>
    {total !== undefined && (
      <Typography
        sx={{ fontSize: 20, color: 'common.black', fontWeight: 'bold', textAlign: align }}
      >
        {total || 0}
      </Typography>
    )}
  </Box>
);

const Snapshots = () => {
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const config = useSelector((state) => state.config?.settings);
  const { aggregatesTotals } = useSelector((state) => state.dashboard);
  const dashboardSettings = config.integrations?.dashboard_settings?.noc;
  const {
    fleetAggregates: { list, pagination },
    fetchingFleetAggregates,
  } = useSelector((state) => state.dashboard);

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    sort_by_name: true,
    upcoming: dashboardSettings?.upcoming || 6,
    type: orgType === 'msOwnerOrg' ? 'location' : 'service',
  });

  const columns = [
    {
      name: (
        <TableLabel
          title={i18n.t('common.owner')}
          subtitle={i18n.t('dashboard.statistics.service_area')}
          description={i18n.t('dashboard.statistics.service_area_description')}
        />
      ),
      selector: (row) => row.name,
      cell: (row) => (orgType === 'msOwnerOrg' ? row?.name || '--' : row?.parent?.name || '--'),
      grow: 1,
      omit: orgType === 'plainOrg',
    },
    {
      name: (
        <TableLabel
          title={i18n.t('common.service')}
          subtitle={i18n.t('dashboard.statistics.vehicle_group')}
          description={i18n.t('dashboard.statistics.vehicle_group_description')}
        />
      ),
      selector: (row) => row.name,
      cell: (row) => row?.group?.name || row.name || '--',
      grow: 2,
      omit: orgType === 'msOwnerOrg',
    },
    {
      name: (
        <TableLabel
          align="center"
          total={aggregatesTotals?.available || 0}
          title={i18n.t('dashboard.statistics.available')}
          subtitle={i18n.t('dashboard.statistics.for_customers')}
          description={i18n.t('dashboard.statistics.available_description')}
        />
      ),
      grow: 1,
      minwidth: '110px',
      maxwidth: '110px',
      cell: (row) => row?.available || row?.totals?.available || 0,
      selector: (row) => row.available,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          total={aggregatesTotals?.inprogress_customer || 0}
          title={i18n.t('common.customer')}
          subtitle={i18n.t('dashboard.statistics.customer_in_progress')}
          description={i18n.t('dashboard.statistics.customer_in_progress_description')}
        />
      ),
      grow: 1,
      minwidth: '91px',
      maxwidth: '91px',
      cell: (row) => row.inprogress_customer || row?.totals?.inprogress_customer || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          title={i18n.t('common.customer')}
          total={aggregatesTotals?.reserved_customer || 0}
          subtitle={i18n.t('dashboard.statistics.customer_late_check_in')}
          description={i18n.t('dashboard.statistics.customer_late_check_in_description')}
        />
      ),
      grow: 1,
      minwidth: '100px',
      maxwidth: '105px',
      cell: (row) => row?.reserved_customer || row?.totals?.reserved_customer || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          title={i18n.t('common.customer')}
          total={aggregatesTotals?.late_return_customer || 0}
          subtitle={i18n.t('dashboard.statistics.customer_late_return')}
          description={i18n.t('dashboard.statistics.customer_late_return_description')}
        />
      ),
      grow: 1,
      minwidth: '95px',
      maxwidth: '95px',
      cell: (row) => row?.late_return_customer || row?.totals?.late_return_customer || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          title={i18n.t('common.customer')}
          total={aggregatesTotals?.upcoming_customer || 0}
          subtitle={i18n.t('dashboard.statistics.customer_upcoming')}
          description={i18n.t('dashboard.statistics.customer_upcoming_description')}
        />
      ),
      grow: 1,
      minwidth: '65px',
      maxwidth: '75px',
      cell: (row) => row.upcoming_customer || row?.totals?.upcoming_customer || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          title={i18n.t('common.employee')}
          total={aggregatesTotals?.in_use_employee || 0}
          subtitle={i18n.t('dashboard.statistics.employee_in_use')}
          description={i18n.t('dashboard.statistics.employee_in_use_description')}
        />
      ),
      grow: 1,
      minwidth: '55px',
      maxwidth: '70px',
      cell: (row) => row.in_use_employee || row?.totals?.in_use_employee || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          title={i18n.t('common.employee')}
          total={aggregatesTotals?.late_return_employee || 0}
          subtitle={i18n.t('dashboard.statistics.employee_late_return')}
          description={i18n.t('dashboard.statistics.employee_late_return_description')}
        />
      ),
      grow: 1,
      minwidth: '95px',
      maxwidth: '95px',
      cell: (row) => row.late_return_employee || row?.totals?.late_return_employee || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          title={i18n.t('common.employee')}
          total={aggregatesTotals?.in_use_other || 0}
          subtitle={i18n.t('dashboard.statistics.other_orgs_in_use')}
          description={i18n.t('dashboard.statistics.other_orgs_in_use_description')}
        />
      ),
      grow: 1,
      minwidth: '75px',
      maxwidth: '75px',
      cell: (row) => row.in_use_other || row?.totals?.in_use_other || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <TableLabel
          align="center"
          title={i18n.t('common.employee')}
          total={aggregatesTotals?.late_return_other || 0}
          subtitle={i18n.t('dashboard.statistics.other_orgs_late_return')}
          description={i18n.t('dashboard.statistics.other_orgs_late_return_description')}
        />
      ),
      grow: 1,
      minwidth: '95px',
      maxwidth: '95px',
      cell: (row) => row?.late_return_other || row?.totals?.return_other || 0,
      style: {
        justifyContent: 'center',
      },
    },
  ];

  const getFleetAggregates = useCallback(
    (params) => {
      dispatch(DashboardActions.fetchAggregates(params));
    },
    [dispatch]
  );

  const getTotals = useCallback(
    (params) => {
      dispatch(DashboardActions.fetchAggregatesTotals(params));
    },
    [dispatch]
  );

  const updateData = useCallback(
    (params) => {
      getFleetAggregates(params);
      getTotals(params);
    },
    [getFleetAggregates, getTotals]
  );

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    setParams({ ...params, ...newParams });
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    setParams({ ...params, ...newParams });
  };

  const handleChangeFilter = debounce((filters) => {
    setParams({ ...filters });
  }, 500);

  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && key !== 'keyword' && delete filters[key]
    );
    if (filters?.keyword) {
      filters.keyword = truncateKeywordSpaces(filters.keyword);
    }
    handleChangeFilter({ ...filters });
  };

  useEffect(() => {
    updateData(params);
  }, [params]); // eslint-disable-line

  const tableExtraStyles = {
    headCells: {
      style: {
        padding: '0 0.25rem',
      },
    },
    cells: {
      style: {
        padding: '0 0.25rem',
      },
    },
    rows: {
      style: {
        minHeight: 44,
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        borderRight: `1px solid ${theme.palette.tables.border}`,
        borderBottom: `1px solid ${theme.palette.tables.border}`,
      },
    },
  };

  return (
    <Card
      enableCollapse
      id="snapshots-widget"
      title="Usage Snapshot"
      headerActionType="vertical"
      onRefresh={() => {
        updateData(params);
      }}
      loading={fetchingFleetAggregates}
    >
      <Box>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid size={12}>
            <Box mb={4}>
              <Grid
                container
                rowSpacing={1}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-end"
              >
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                  <Grid
                    container
                    wrap="nowrap"
                    rowSpacing={1}
                    alignItems="flex-end"
                    columnSpacing={{ xs: 1 }}
                  >
                    <Grid>
                      <SearchIcon sx={{ color: 'text.primary', fontSize: 16 }} />
                    </Grid>
                    <Grid style={{ width: '100%' }}>
                      <SearchField
                        allowClear
                        margin="none"
                        label={
                          orgType === 'msOwnerOrg'
                            ? i18n.t('dashboard.search_service_area')
                            : i18n.t('dashboard.search_vehicle_group')
                        }
                        field={{
                          onChange: search,
                          name: 'keyword',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid size={12}>
            <Paper elevation={2} sx={{ boxShadow: '0px 3px 6px #0000001A' }}>
              <Table
                data={list}
                columns={columns}
                page={params.page}
                limit={params.limit}
                extraStyles={tableExtraStyles}
                loading={fetchingFleetAggregates}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

TableLabel.propTypes = {
  total: PropTypes.number,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  align: PropTypes.oneOf(['start', 'center', 'end']),
};

export default Snapshots;
